import { Order } from 'store/types'

export type InsuranceVerification = NonNullable<Order['insuranceVerificationData']>[number]

/**
 * Mirrors the backend "Outsource: Hide Medicare Advantage / Medicaid Orders" policy, which is
 * strictly fail-closed: an order is shown to outsource users ONLY when it has at least one
 * insurance verification and every payer position's latest verification explicitly records
 * `isMedicareAdvantage === false && isMedicaid === false`. Anything else (no verification,
 * pending, failed, null flags, or a Medicare Advantage / Medicaid payer) hides the order.
 *
 * `insuranceVerificationData` is the (up to) 3 most-recent verifications ordered created_at DESC,
 * so the first entry seen for each `type` is that position's latest verification.
 */
export function latestVerificationsPerType(order?: Order | null): InsuranceVerification[] {
  const verifications = order?.insuranceVerificationData ?? []
  const latestByType = new Map<string, InsuranceVerification>()
  for (const verification of verifications) {
    if (!latestByType.has(verification.type)) {
      latestByType.set(verification.type, verification)
    }
  }
  return [...latestByType.values()]
}

const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value

const payerName = (order: Order | null | undefined, verification: InsuranceVerification): string => {
  const payer = order?.payers?.find((p: any) => p?.id === verification.dmePayerId)
  return payer?.name || `${capitalize(verification.type)} payer`
}

export const ELIGIBILITY_FLAG_KEYS = ['isMedicareAdvantage', 'isMedicaid'] as const
export type EligibilityFlagKey = (typeof ELIGIBILITY_FLAG_KEYS)[number]

export const ELIGIBILITY_FLAG_LABELS: Record<EligibilityFlagKey, string> = {
  isMedicareAdvantage: 'Medicare Advantage',
  isMedicaid: 'Medicaid',
}

/** Current value of an eligibility flag: `true` (detected), `false` (cleared), `null` (undetermined). */
export type PayerFlag = { key: EligibilityFlagKey; label: string; value: boolean | null }

export type EditablePayer = {
  verification: InsuranceVerification
  name: string
  type: string
  flags: PayerFlag[]
}

/**
 * Every payer whose order has a latest verification row we can write flags to, with both
 * eligibility flags and their current value — `true` (detected), `false` (cleared), or `null`
 * (undetermined: COX/PSG, an AI-disabled payer, or a failed lambda). Because the policy is
 * strictly fail-closed — only an explicit `false` on every flag makes an order visible — a `null`
 * flag hides the order just like `true` does, so it must be editable too; otherwise those orders
 * would have no grant path even though the backend accepts the write. Orders with no verification
 * row at all have nothing to write to and are not listed here.
 */
export function getEditablePayers(order?: Order | null): EditablePayer[] {
  if (!order) {
    return []
  }
  return latestVerificationsPerType(order).map((v) => ({
    verification: v,
    name: payerName(order, v),
    type: v.type,
    flags: ELIGIBILITY_FLAG_KEYS.map((key) => ({
      key,
      label: ELIGIBILITY_FLAG_LABELS[key],
      value: typeof v[key] === 'boolean' ? (v[key] as boolean) : null,
    })),
  }))
}

/**
 * The verification-derived hidden reason, computed from the loaded verification rows (this is
 * the part the client can name precisely — which flag, which program). Payer-approval and
 * unlinked-payer reasons are NOT visible in this data; they come from the server verdict below.
 */
function getVerificationHiddenReason(order?: Order | null): string | null {
  const verifications = order?.insuranceVerificationData ?? []
  if (verifications.length === 0) {
    return 'no insurance verification has been performed on this order yet'
  }

  const latest = latestVerificationsPerType(order)

  const hasMedicareAdvantage = latest.some((v) => v.isMedicareAdvantage === true)
  const hasMedicaid = latest.some((v) => v.isMedicaid === true)
  if (hasMedicareAdvantage || hasMedicaid) {
    const label =
      hasMedicareAdvantage && hasMedicaid
        ? 'Medicare Advantage and Medicaid'
        : hasMedicareAdvantage
          ? 'Medicare Advantage'
          : 'Medicaid'
    return `one of the payers is ${label}`
  }

  // An explicit `false` on both flags is required to clear a payer; null/undefined means the
  // eligibility check hasn't produced a definitive answer (pending or failed verification).
  const hasUnclearedPayer = latest.some(
    (v) => v.isMedicareAdvantage !== false || v.isMedicaid !== false,
  )
  if (hasUnclearedPayer) {
    return 'insurance eligibility has not been confirmed for every payer yet'
  }

  return null
}

/**
 * The human-readable reason(s) an order is hidden from outsource users, or `null` when the
 * order is visible to them. The server's live per-probe verdict (`order.outsourceVisibility`)
 * is authoritative when present — it also knows the blocked-payer and unlinked-payer reasons
 * the client-side verification mirror cannot see. The mirror remains as the wording source for
 * verification reasons and as a fallback for older payloads without the server field.
 */
export function getOutsourceHiddenReason(order?: Order | null): string | null {
  const verdict = order?.outsourceVisibility
  if (!verdict) {
    return getVerificationHiddenReason(order)
  }
  if (verdict.visible) {
    return null
  }

  const reasons: string[] = []
  if (!verdict.hasVerification) {
    reasons.push('no insurance verification has been performed on this order yet')
  } else if (verdict.hasUnclearedPayer) {
    reasons.push(
      getVerificationHiddenReason(order) ??
        'insurance eligibility has not been confirmed for every payer yet',
    )
  }
  if (verdict.hasBlockedPayer) {
    reasons.push('one of the payers has not been approved for outsource work')
  }
  if (verdict.hasUnlinkedPayer) {
    reasons.push('one of the payers is not linked to a lab payer, so it cannot be classified')
  }
  // The server says hidden, so never return null here; the generic fallback should be unreachable.
  return reasons.length ? reasons.join(', and ') : 'of the order access policy'
}
